import { format } from 'date-fns'

import { InternalFeatureMap } from '../../../api/features'
import { InternalGameScreenshot } from '../../../api/screenshot-links'
import { InternalCategory, InternalCategoryFeature } from '../../../types/InternalCategory'
import { InternalConcept } from '../../../types/InternalTag'
import { ConceptMatchGroup, ConceptsReadBack, ScreenshotConcept, ScreenshotFeature } from '../../analysis-screenshots/types/AnalysisScreenshots'
import { BulkAssignCounts, BulkAssignEditView, BulkAssignPickerFeature, BulkAssignStoredChoice } from '../types/ImplementationBulkAssign'

export const bulkAssignMarketIso = 'us'

// Core and Meta are picked choice by choice; any other feature with all its choices at once.
export const choiceByChoiceFeatureLegacyIds = [155, 154]

export const isChoiceByChoiceFeature = (legacyId: number) => choiceByChoiceFeatureLegacyIds.includes(legacyId)

export const bulkAssignGameFields = [
  'appId',
  'artist',
  'name',
  'icons',
  'id',
  'markets',
  'genreId',
  'tags',
  'conventionalSubgenre',
  'conventionalSubgenreId',
  'sdranks',
  'sranks',
]

const unique = <T>(values: T[]) => Array.from(new Set(values))

type PickerCategory = Pick<InternalCategory, 'id' | 'name'> & { icon?: { url?: string | null } | null }

// As legacy: the categories' features with their active choices that some screenshot has; features left without a choice are left out.
export const toPickerFeatures = (
  categories: PickerCategory[],
  featuresByCategory: Record<string, InternalCategoryFeature[]>,
  allowedChoiceIds: number[]
): BulkAssignPickerFeature[] => {
  const allowed = new Set(allowedChoiceIds)
  return categories.flatMap((category) =>
    (featuresByCategory[category.id] ?? []).flatMap((feature) => {
      const choices = (feature.choices ?? [])
        .filter((choice) => choice.active && allowed.has(choice.weightId))
        .map(({ weightId, choice }) => ({ weightId, choice }))
      if (!choices.length) return []
      return [
        {
          legacyId: feature.legacyId,
          name: feature.name,
          categoryId: category.id,
          categoryName: category.name,
          iconUrl: category.icon?.url ?? undefined,
          tags: Array.isArray(feature.tags) ? (feature.tags as string[]) : [],
          choices,
        },
      ]
    })
  )
}

const pickedChoices = (feature: BulkAssignPickerFeature, choiceIds: number[]) => feature.choices.filter(({ weightId }) => choiceIds.includes(weightId))

// Core and Meta count their picked choices' screenshots; otherwise the feature's.
export const getPickerFeatureCount = (feature: BulkAssignPickerFeature, choiceIds: number[], counts?: BulkAssignCounts) => {
  if (!counts) return undefined
  const picked = isChoiceByChoiceFeature(feature.legacyId) ? pickedChoices(feature, choiceIds) : []
  if (picked.length) return picked.reduce((sum, { weightId }) => sum + (counts.byChoice[String(weightId)] ?? 0), 0)
  return counts.byFeature[String(feature.legacyId)] ?? 0
}

export const isFeaturePicked = (feature: BulkAssignPickerFeature, choiceIds: number[]) => pickedChoices(feature, choiceIds).length > 0

export const isFeatureFullyPicked = (feature: BulkAssignPickerFeature, choiceIds: number[]) =>
  pickedChoices(feature, choiceIds).length === feature.choices.length

export const countPickedFeatures = (features: BulkAssignPickerFeature[], choiceIds: number[]) =>
  features.filter((feature) => isFeaturePicked(feature, choiceIds)).length

// Picks every choice of the feature, or drops them all when every one is picked.
export const toggleFeatureChoices = (choiceIds: number[], feature: BulkAssignPickerFeature) => {
  const featureChoiceIds = feature.choices.map(({ weightId }) => weightId)
  if (isFeatureFullyPicked(feature, choiceIds)) return choiceIds.filter((id) => !featureChoiceIds.includes(id))
  return unique([...choiceIds, ...featureChoiceIds])
}

export const toggleValue = <T>(values: T[], value: T) => (values.includes(value) ? values.filter((candidate) => candidate !== value) : [...values, value])

// What the summary shows of the picked choices: each feature once, with its picked choices.
export const describePickedChoices = (choiceIds: number[], storedChoices: BulkAssignStoredChoice[], featureMap?: InternalFeatureMap) => {
  const featureNames = new Map(Object.values(featureMap ?? {}).map((feature) => [feature.legacyId, feature.name]))
  const byFeature = new Map<number, string[]>()
  choiceIds.forEach((id) => {
    const choice = storedChoices.find(({ legacyId }) => legacyId === id)
    const featureLegacyId = choice?.featureLegacyId ?? -1
    byFeature.set(featureLegacyId, [...(byFeature.get(featureLegacyId) ?? []), choice?.label ?? `#${id}`])
  })
  return Array.from(byFeature.entries()).map(([legacyId, labels]) => ({
    legacyId,
    name: legacyId < 0 ? undefined : featureNames.get(legacyId) ?? `#${legacyId}`,
    choices: labels,
  }))
}

// Legacy's features-first filter: with concepts picked too, only the screenshots having any of them.
export const filterByAnyConcept = (screenshots: InternalGameScreenshot[], conceptIds: string[]) =>
  conceptIds.length ? screenshots.filter((screenshot) => (screenshot.conceptIds ?? []).some((id) => conceptIds.includes(id))) : screenshots

const compareValues = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)

// Legacy's order: game, version release date, id.
export const sortBulkAssignScreenshots = (screenshots: InternalGameScreenshot[]) =>
  [...screenshots].sort(
    (a, b) => compareValues(a.gameId, b.gameId) || compareValues(a.versionReleaseDate ?? 0, b.versionReleaseDate ?? 0) || compareValues(a.id, b.id)
  )

export const getScreenshotGameIds = (screenshots: InternalGameScreenshot[]) => unique(screenshots.map(({ gameId }) => gameId))

export const filterByGames = (screenshots: InternalGameScreenshot[], gameIds: string[]) => {
  const ids = new Set(gameIds)
  return screenshots.filter(({ gameId }) => ids.has(gameId))
}

// Legacy's games-first text filter: the screenshots having any matched feature. Null leaves them all.
export const filterByFeatureLegacyIds = (screenshots: InternalGameScreenshot[], legacyIds: Set<number> | null) =>
  legacyIds ? screenshots.filter((screenshot) => Object.keys(screenshot.features ?? {}).some((legacyId) => legacyIds.has(Number(legacyId)))) : screenshots

export const getScreenshotFeatureLegacyIds = (screenshots: InternalGameScreenshot[]) =>
  unique(screenshots.flatMap((screenshot) => Object.keys(screenshot.features ?? {}).map(Number)))

const toScreenshotFeature = (legacyId: number, weightId: number, featureMap: Map<number, InternalFeatureMap[string]>): ScreenshotFeature => {
  const feature = featureMap.get(legacyId)
  return {
    legacyId,
    name: feature?.name ?? `#${legacyId}`,
    weightId,
    choice: feature?.choices?.find((choice) => choice.weightId === weightId)?.choice ?? String(weightId),
  }
}

export const toFeatureMapByLegacyId = (featureMap?: InternalFeatureMap) =>
  new Map(Object.values(featureMap ?? {}).map((feature) => [feature.legacyId, feature]))

export const toEditView = (
  screenshot: InternalGameScreenshot,
  featuresByLegacyId: Map<number, InternalFeatureMap[string]>,
  conceptsById: Map<string, ScreenshotConcept>
): BulkAssignEditView => {
  const conceptIds = screenshot.conceptIds ?? []
  return {
    screenshot,
    item: { screenshotId: screenshot.id, conceptIds },
    features: Object.entries(screenshot.features ?? {}).map(([legacyId, weightId]) => toScreenshotFeature(Number(legacyId), weightId, featuresByLegacyId)),
    concepts: conceptIds.flatMap((id) => {
      const concept = conceptsById.get(id)
      return concept ? [concept] : []
    }),
    unknownConceptIds: conceptIds.filter((id) => !conceptsById.has(id)),
  }
}

// The concepts' side of a write: `concept.screenshotIds` should list (or no longer list) each screenshot.
export const getConceptListReadBack = (groups: ConceptMatchGroup[], concepts: InternalConcept[], expectMatched: boolean): ConceptsReadBack => {
  const byId = new Map(concepts.map((concept) => [concept.id, new Set(concept.screenshotIds ?? [])]))
  const notStored: Record<string, string[]> = {}
  groups.forEach(({ screenshotsIds, conceptsIds }) =>
    conceptsIds.forEach((conceptId) => {
      const listed = byId.get(conceptId) ?? new Set<string>()
      screenshotsIds.forEach((screenshotId) => {
        if (listed.has(screenshotId) !== expectMatched) notStored[screenshotId] = unique([...(notStored[screenshotId] ?? []), conceptId])
      })
    })
  )
  return { notStored }
}

export const mergeConceptsReadBacks = (...readBacks: ConceptsReadBack[]): ConceptsReadBack => {
  const notStored: Record<string, string[]> = {}
  readBacks.forEach((readBack) =>
    Object.entries(readBack.notStored).forEach(([screenshotId, conceptIds]) => {
      notStored[screenshotId] = unique([...(notStored[screenshotId] ?? []), ...conceptIds])
    })
  )
  return { notStored }
}

// Legacy's "08 Feb 2021".
export const formatReleaseDate = (timestamp?: number | null) => (timestamp ? format(timestamp, 'dd MMM yyyy') : undefined)
